import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "@/config/config";
import {
  enterSafehouse as enterSafehouseContract,
  exitSafehouse as exitSafehouseContract,
  getSafehouseUserInfo,
  SafehouseUserInfo,
} from "@/helper/contractFunctions/MafiaSafehouse";
import { getMyProfile } from "@/features/profile";

export interface SafehouseState {
  isEnteringSafehouse: boolean;
  isExitingSafehouse: boolean;
  safeUntil: number;
  duration: number;
  nextSafehouseTime: number;
}

const initialState: SafehouseState = {
  isEnteringSafehouse: false,
  isExitingSafehouse: false,
  safeUntil: 0,
  duration: 0,
  nextSafehouseTime: 0,
};

const applyUserInfo = (
  state: SafehouseState,
  info: SafehouseUserInfo | null | undefined
) => {
  if (!info) return;
  state.safeUntil = info.safeUntil;
  state.duration = info.duration;
  state.nextSafehouseTime = info.nextSafehouseTime;
};

const refreshUserInfo = async (
  account: string,
  signMsg?: string,
  signature?: string
) => {
  if (!signMsg || !signature) return null;
  try {
    return await getSafehouseUserInfo(account, signMsg, signature);
  } catch (error) {
    console.error("Failed to read safehouse user info", error);
    return null;
  }
};

export const getSafehouseUserInfoAction = createAsyncThunk(
  "safehouse/getSafehouseUserInfo",
  async ({
    account,
    signMsg,
    signature,
  }: {
    account: string;
    signMsg: string;
    signature: string;
  }) => getSafehouseUserInfo(account, signMsg, signature)
);

export const enterSafehouseAction = createAsyncThunk(
  "safehouse/enterSafehouseAction",
  async (
    {
      account,
      hours,
      profileName,
      signMsg,
      signature,
    }: {
      account: string;
      hours: number;
      profileName: string;
      signMsg?: string;
      signature?: string;
    },
    { dispatch }
  ) => {
    if (config.safehouseAddress && config.safehouseAddress.length > 0) {
      await enterSafehouseContract(account, hours);
      if (profileName) {
        await dispatch(getMyProfile(profileName));
      }
      return refreshUserInfo(account, signMsg, signature);
    }
    return null;
  }
);

export const exitSafehouseAction = createAsyncThunk(
  "safehouse/exitSafehouseAction",
  async (
    {
      account,
      profileName,
      signMsg,
      signature,
    }: {
      account: string;
      profileName: string;
      signMsg?: string;
      signature?: string;
    },
    { dispatch }
  ) => {
    if (config.safehouseAddress && config.safehouseAddress.length > 0) {
      await exitSafehouseContract(account);
      if (profileName) {
        await dispatch(getMyProfile(profileName));
      }
      return refreshUserInfo(account, signMsg, signature);
    }
    return null;
  }
);

const safehouseSlice = createSlice({
  name: "safehouse",
  initialState,
  reducers: {
    clearSafehouseInfo(state) {
      state.safeUntil = 0;
      state.duration = 0;
      state.nextSafehouseTime = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSafehouseUserInfoAction.fulfilled, (state, { payload }) => {
        applyUserInfo(state, payload);
      })
      .addCase(enterSafehouseAction.pending, (state) => {
        state.isEnteringSafehouse = true;
      })
      .addCase(enterSafehouseAction.fulfilled, (state, { payload }) => {
        state.isEnteringSafehouse = false;
        applyUserInfo(state, payload);
      })
      .addCase(enterSafehouseAction.rejected, (state) => {
        state.isEnteringSafehouse = false;
      })
      .addCase(exitSafehouseAction.pending, (state) => {
        state.isExitingSafehouse = true;
      })
      .addCase(exitSafehouseAction.fulfilled, (state, { payload }) => {
        state.isExitingSafehouse = false;
        applyUserInfo(state, payload);
      })
      .addCase(exitSafehouseAction.rejected, (state) => {
        state.isExitingSafehouse = false;
      });
  },
});

export const { clearSafehouseInfo } = safehouseSlice.actions;

export default safehouseSlice.reducer;
